import { Select, Space, Tag, Typography } from 'antd';
import {
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import { PMP_TAG_COLOR } from '~/app/components/maintenance/pmp-tag-colors';
import type { WizardTask } from '~/app/components/maintenance/preventive/wizard/types';

const { Text } = Typography;

type StepTasksCardProps = {
  task: WizardTask;
  index: number;
  triggerOptions: Array<{ label: string; value: string }>;
  isCreatingTrigger: boolean;
  onRemove: () => void;
  onTriggerChange: (triggerId: string, triggerLabel: string) => void;
  onCreateTrigger: () => void;
};

export function StepTasksCard({
  task,
  index,
  triggerOptions,
  isCreatingTrigger,
  onRemove,
  onTriggerChange,
  onCreateTrigger,
}: StepTasksCardProps) {
  const { t } = useTranslation('maintenance');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.taskId });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderBottom: isCreatingTrigger ? 'none' : '1px solid #f0f0f0',
        background: isDragging
          ? '#fafafa'
          : isCreatingTrigger
            ? '#fafafa'
            : '#fff',
      }}
    >
      <HolderOutlined
        {...attributes}
        {...listeners}
        style={{ cursor: 'grab', color: '#999', fontSize: 16 }}
      />
      <Text strong style={{ width: 28, textAlign: 'center' }}>
        {index + 1}
      </Text>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Space size={8} wrap>
          {task.workTypeLabel ? (
            <Tag color={PMP_TAG_COLOR.workType}>{task.workTypeLabel}</Tag>
          ) : null}
          <Text strong>{task.name}</Text>
          {task.mandatory ? (
            <Tag color="green">{t('tasks.mandatory')}</Tag>
          ) : null}
        </Space>
        {task.subtaskNames && task.subtaskNames.length > 0 ? (
          <div
            style={{
              marginTop: 6,
              maxHeight: 72,
              overflowY: 'auto',
              paddingLeft: 2,
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              {task.subtaskNames
                .map((name, i) => `${i + 1}. ${name}`)
                .join(' · ')}
            </Text>
          </div>
        ) : null}
      </div>
      <Space size={8} wrap style={{ justifyContent: 'flex-end' }}>
        {task.triggerLabel ? (
          <Tag color="default" style={{ marginInlineEnd: 0 }}>
            {task.triggerLabel}
          </Tag>
        ) : null}
        <Select
          style={{ minWidth: 200 }}
          placeholder={t('wizard.tasks.selectTrigger')}
          value={task.triggerId || undefined}
          options={triggerOptions}
          onChange={(value) => {
            const label =
              triggerOptions.find((opt) => opt.value === value)?.label ?? value;
            onTriggerChange(value, label);
          }}
          status={task.triggerId ? undefined : 'error'}
        />
        <Button
          type={isCreatingTrigger ? 'primary' : 'dashed'}
          size="small"
          icon={<PlusOutlined />}
          onClick={onCreateTrigger}
        >
          {t('wizard.tasks.createTrigger')}
        </Button>
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={onRemove}
          size="small"
          aria-label={t('wizard.tasks.removeTask')}
        />
      </Space>
    </div>
  );
}
